import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n/I18nProvider'
import type { MessageKey } from '../i18n/messages'
import { DateLisible, Nombre } from '../components/Nombre'
import { Alert, Badge, Button, Card, DataTable, PageHeader, SelectField, Stack, useToast } from '../components/ui'
import type { Column } from '../components/ui'
import { billingApi, toneOf } from './billingApi'
import type { InvoiceStatus, InvoiceSummary, SellerProfile } from './billingApi'

const STATUTS: readonly InvoiceStatus[] = ['unpaid', 'overdue', 'partial', 'paid', 'cancelled', 'credit_note']

/**
 * Toutes les factures et tous les avoirs de la plateforme, les plus récents d'abord.
 *
 * L'ÉTAT EST CELUI DE LA BASE (`saas.invoice_status`), dérivé des paiements, des avoirs et de
 * la date : l'écran ne décide pas qu'une facture est échue.
 *
 * LE VENDEUR INCOMPLET EST DIT EN TÊTE. Tant que le matricule fiscal ou l'adresse de la
 * plateforme manquent, aucune facture ne part — pas même au renouvellement, où la tâche passe
 * l'abonnement en paiement attendu sans facture. Le taire laisserait croire que les clients
 * ont été facturés.
 */
export function InvoicesPage() {
  const { t, describeError } = useI18n()
  const { session } = useAuth()
  const { notify } = useToast()
  const navigate = useNavigate()

  const token = session?.token ?? ''

  const [factures, setFactures] = useState<InvoiceSummary[]>([])
  const [vendeur, setVendeur] = useState<SellerProfile | null>(null)
  const [statut, setStatut] = useState('')
  const [chargement, setChargement] = useState(true)

  const charger = useCallback(() => {
    if (token === '') return
    setChargement(true)
    Promise.all([billingApi.listInvoices(token), billingApi.getSeller(token)])
      .then(([liste, profil]) => {
        setFactures(liste)
        setVendeur(profil)
      })
      .catch((cause: unknown) => notify(describeError(cause), 'erreur'))
      .finally(() => setChargement(false))
  }, [token, notify, describeError])

  useEffect(charger, [charger])

  const visibles = useMemo(
    () => (statut === '' ? factures : factures.filter((f) => f.status === statut)),
    [factures, statut],
  )

  const colonnes: Column<InvoiceSummary>[] = [
    {
      key: 'number',
      header: t('billing.col.number'),
      render: (f) => (
        <Link to={`/platform/invoices/${f.id}`} className="font-medium">
          <span dir="ltr">{f.number}</span>
        </Link>
      ),
    },
    {
      key: 'client',
      header: t('billing.col.client'),
      render: (f) => <Link to={`/platform/clients/${f.organizationId}`}>{f.clientName}</Link>,
    },
    { key: 'issuedOn', header: t('billing.col.issuedOn'), render: (f) => <DateLisible valeur={f.issuedOn} />, secondary: true },
    {
      key: 'dueOn',
      header: t('billing.col.dueOn'),
      render: (f) => (f.kind === 'invoice' ? <DateLisible valeur={f.dueOn} /> : '—'),
      secondary: true,
    },
    { key: 'total', header: t('billing.col.total'), numeric: true, render: (f) => <Nombre valeur={f.totalTtc} decimales={3} /> },
    {
      key: 'balance',
      header: t('billing.col.balance'),
      numeric: true,
      render: (f) => (f.kind === 'invoice' ? <Nombre valeur={f.balance} decimales={3} /> : '—'),
    },
    {
      key: 'status',
      header: t('billing.col.status'),
      render: (f) => <Badge tone={toneOf(f.status)}>{t(`billing.status.${f.status}` as MessageKey)}</Badge>,
    },
  ]

  return (
    <Stack>
      <PageHeader
        title={t('billing.invoices.title')}
        description={t('billing.invoices.description')}
        actions={
          <Button variant="secondaire" onClick={() => navigate('/platform/billing')}>
            {t('billing.seller.title')}
          </Button>
        }
      />

      {vendeur !== null && !vendeur.isComplete && (
        <Alert tone="avertissement" title={t('billing.seller.incompleteTitle')}>
          {t('billing.seller.incomplete')} <Link to="/platform/billing">{t('billing.seller.complete')}</Link>
        </Alert>
      )}

      <Card
        title={t('billing.invoices.list')}
      >
        <Stack gap={3}>
          <div style={{ maxWidth: '16rem' }}>
            <SelectField
              label={t('billing.col.status')}
              value={statut}
              onChange={setStatut}
              options={[
                { value: '', label: t('billing.status.all') },
                ...STATUTS.map((s) => ({ value: s, label: t(`billing.status.${s}` as MessageKey) })),
              ]}
            />
          </div>
          {chargement ? (
            <p className="text-muted">{t('common.loading')}</p>
          ) : (
            <DataTable columns={colonnes} rows={visibles} rowKey={(f) => f.id} emptyLabel={t('billing.invoices.empty')} />
          )}
        </Stack>
      </Card>
    </Stack>
  )
}
